import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { SiteModel } from '../../../models/site.model';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { ResolvedAddress } from '../../../shared/address-lookup/address-lookup.component';
import { RBACService } from '../../../shared/rbac.service';

declare var google;

@Component({
  selector: 'app-sites-details-map',
  templateUrl: './sites-details-map.component.html',
  styleUrls: ['./sites-details-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesDetailsMapComponent implements OnInit {
  inProgress: boolean = false;

  // the site model placeholder
  site: SiteModel = new SiteModel();

  // Type of google.maps.LatLng
  geofenceCoordinates = [];

  coordinatesSetAtListener: any;
  coordinatesInsertAtListener: any;

  // Sign-out zone circle overlay
  signOutZoneCircle: any;

  // variables used to manipulate the map
  @ViewChild('map') mapElement: ElementRef;
  map: any;
  polygon: any;

  // Listener for map clicks while drawing a new geofence. Replaces the decommissioned
  // google.maps.drawing.DrawingManager (removed from the Maps JavaScript API as of v3.65).
  mapClickListener: any;

  // Whether the user is currently drawing a new geofence by clicking points on the map.
  isDrawing: boolean = false;

  // On-map control button that lets the user finish drawing. It is added to the map once and shown
  // only while drawing and a valid geofence (3+ plotted points) exists.
  finishDrawingControl: HTMLButtonElement | null = null;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      this.router.navigate(['/sites']);
      return;
    }

    // Get the site id from the route params.
    const parent_id: number = Number(
      this.route.parent.snapshot.params['parent_id']
    );
    const child_id: number = Number(
      this.route.parent.snapshot.params['child_id']
    );

    // Check if we are updating a site or creating a new one.
    if (
      child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        parent_id)
    ) {
      // Store the site id.
      this.site.id = child_id ? child_id : parent_id;
      // Get the data from the API.
      this.api
        .makeRequest('get', 'v1/sites/' + this.site.id)
        .then(
          (response: any): void => {
            // apply the data
            this.site.apply(response.data);

            // Load the Google Maps API (all libraries) then configure the map.
            this.utils
              .loadGoogleMaps()
              .then(
                (): void => {
                  this.loadMapConfiguration();
                }
              )
              .catch(
                (error): void => {
                  console.error('Geofence map could not be loaded:', error);
                  this.utils.showModal(
                    'Error',
                    'The map could not be loaded. Please refresh the page and try again.'
                  );
                }
              );
          }
        )
        .catch(
          (err: any): void => {
            this.utils.showModal('Error', err.error.message);
            this.router.navigate(['sites']);
          }
        );
    } else {
      this.router.navigate(['/sites']);
      return;
    }
  }

  /**
   * Create or update the site.
   * @param form
   */
  onSubmit(form: NgForm): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_EDIT
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (!form.valid) {
      this.utils.showModal(
        'Form Error',
        'Please make sure you enter all fields with valid information.'
      );
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    // If the user is still in drawing mode, finishing implicitly on save: exit drawing mode so the
    // click listener and on-map control are cleaned up before the geofence is stored.
    if (this.isDrawing) {
      this.stopDrawing();
    }

    // update the geofence
    this.storePolygonPaths();

    // Check if the site should be updated.
    if (this.site.id) {
      this.api
        .makeRequest('put', 'v1/sites/' + this.site.id, this.site)
        .then(
          (response: any): void => {
            this.utils.showToast('The geofence map was updated.');
            this.inProgress = false;
          }
        )
        .catch(
          (err: any): void => {
            this.inProgress = false;
          }
        );
    } else {
      this.inProgress = false;
    }
  }

  /**
   * Load the map configuration.
   */
  private loadMapConfiguration(): void {
    // set the default geofence bounds and coordinates
    const geofenceBounds = new google.maps.LatLngBounds();

    this.geofenceCoordinates.length = 0;

    // if the currently loaded site has a geofence, load it instead
    if (this.site.geofence) {
      const points: string[] = this.site.geofence.split(',');
      if (points.length) {
        // clear the default geofence values and add the new values
        // geofenceCoordinates.length = 0;
        points.forEach(
          (point: string, i: number): void => {
            const coordinates_data: string[] = point.split(' ');
            const coordinates = new google.maps.LatLng(
              coordinates_data[0],
              coordinates_data[1]
            );
            geofenceBounds.extend(coordinates);
            this.geofenceCoordinates.push(coordinates);
          }
        );
      }
    } else {
      geofenceBounds.extend(new google.maps.LatLng(-36.848552, 174.762105));
    }

    // initialize the map object
    this.map = new google.maps.Map(this.mapElement.nativeElement, {
      zoom: 17,
      center: geofenceBounds.getCenter(),
      // Disable clickable place-of-interest icons/labels so clicking a business name does not open an
      // info window or plot a geofence vertex on top of it.
      clickableIcons: false,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false
    });

    // Build the on-map "Finish Drawing" control (hidden until a valid geofence exists).
    this.createFinishDrawingControl();

    this.calculateGeofenceSize();

    // if there's a polygon, construct and add it to the map
    if (this.geofenceCoordinates.length > 1) {
      // Construct the polygon.
      this.polygon = new google.maps.Polygon({
        paths: this.geofenceCoordinates,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillOpacity: 0.35,
        editable: true
      });
      this.polygon.setMap(this.map);

      this.addGeofenceEditEvents();

      this.map.fitBounds(geofenceBounds);
      this.drawSignOutZoneOverlay();
    } else {
      // No geofence yet: start drawing mode so the user can click out a polygon.
      this.startDrawing();

      if (this.site.location) {
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode(
          { address: this.site.location },
          (results: any, status: any): void => {
            if (status === 'OK') {
              this.map.setCenter(results[0].geometry.location);
            }
          }
        );
      }
    }
  }

  /**
   * Returns a location bias (the current map viewport bounds) for the address lookup, so address
   * predictions are biased toward what the user is currently viewing. Passed to the shared
   * app-address-lookup component as a provider so it reads live bounds at query time.
   */
  getMapBoundsBias = (): any => {
    return this.map ? this.map.getBounds() : undefined;
  };

  /**
   * Handle an address selected from the shared address lookup: store the formatted address and
   * recenter the map on the selected location.
   * @param address The resolved address emitted by the lookup component.
   */
  onAddressSelected(address: ResolvedAddress): void {
    this.site.location = address.formattedAddress;

    if (address.location) {
      this.map.setCenter(address.location);
      this.map.setZoom(18);
    }
  }

  /**
   * Begin drawing a new geofence. Each click on the map adds a vertex to an editable polygon.
   * This replaces the decommissioned google.maps.drawing.DrawingManager: the Drawing Library was
   * removed from the Maps JavaScript API as of v3.65 (May 2026).
   */
  startDrawing(): void {
    // Already drawing; nothing to do.
    if (this.isDrawing) {
      return;
    }

    this.isDrawing = true;

    // Add each clicked point as a vertex of the geofence polygon.
    this.mapClickListener = this.map.addListener(
      'click',
      (event: any): void => {
        // Ignore clicks on points of interest (place labels/icons). These clicks carry a placeId and
        // would otherwise plot a geofence point on top of a business name. Suppress the default POI
        // info window while drawing as well.
        if (event.placeId) {
          if (typeof event.stop === 'function') {
            event.stop();
          }
          return;
        }

        // Lazily create the editable polygon on the first click.
        if (!this.polygon) {
          this.polygon = new google.maps.Polygon({
            paths: [event.latLng],
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillOpacity: 0.35,
            editable: true,
            map: this.map
          });
          // Keep the stored geofence in sync as the user edits vertices.
          this.addGeofenceEditEvents();
        } else {
          // Append the new vertex to the existing polygon path.
          this.polygon.getPath().push(event.latLng);
        }

        this.storePolygonPaths();
        // Reveal the on-map finish control once the shape is a valid geofence.
        this.updateFinishDrawingControlVisibility();
      }
    );
  }

  /**
   * Finish drawing the geofence. Validates that enough points were placed, then exits drawing mode
   * so the user can fine tune vertices without adding new ones on every map click.
   */
  finishDrawing(): void {
    // A polygon needs at least three vertices to enclose an area.
    if (!this.polygon || this.polygon.getPath().getLength() < 3) {
      this.utils.showModal(
        'Geofence',
        'Please click at least three points on the map to outline the geofence area.'
      );
      return;
    }

    this.stopDrawing();
    this.storePolygonPaths();
  }

  /**
   * Stop drawing mode, remove the map click listener and hide the on-map finish control.
   */
  private stopDrawing(): void {
    this.isDrawing = false;
    if (this.mapClickListener) {
      google.maps.event.removeListener(this.mapClickListener);
      this.mapClickListener = null;
    }
    this.updateFinishDrawingControlVisibility();
  }

  /**
   * Build the on-map "Finish Drawing" control and add it to the map's top-center control slot.
   * The control is created once and toggled via updateFinishDrawingControlVisibility().
   * @private
   */
  private createFinishDrawingControl(): void {
    // Only build the control once.
    if (this.finishDrawingControl) {
      return;
    }

    const button: HTMLButtonElement = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Finish Drawing';
    // Styled (and pulsed) via the global .geofence-finish-control class. The button lives in the
    // Google Maps control container, outside Angular's component style encapsulation.
    button.className = 'geofence-finish-control';

    // Prevent the click from also registering as a map click (which would add a vertex).
    button.addEventListener(
      'click',
      (event: MouseEvent): void => {
        event.stopPropagation();
        this.finishDrawing();
      }
    );

    this.finishDrawingControl = button;
    // Hidden until a valid geofence exists.
    button.style.display = 'none';
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(button);
  }

  /**
   * Show the on-map finish control only while drawing and once a valid geofence (3+ points) exists.
   * @private
   */
  private updateFinishDrawingControlVisibility(): void {
    if (!this.finishDrawingControl) {
      return;
    }
    const hasValidGeofence: boolean =
      !!this.polygon && this.polygon.getPath().getLength() >= 3;
    this.finishDrawingControl.style.display =
      this.isDrawing && hasValidGeofence ? 'inline-block' : 'none';
  }

  /**
   * Subscribe to event listeners to update the polygon paths.
   * @private
   */
  private addGeofenceEditEvents(): void {
    // Triggered when a point is moved.
    this.coordinatesSetAtListener = google.maps.event.addListener(
      this.polygon.getPath(),
      'set_at',
      (): void => {
        this.storePolygonPaths();
      }
    );
    // Triggered when a point is inserted.
    this.coordinatesInsertAtListener = google.maps.event.addListener(
      this.polygon.getPath(),
      'insert_at',
      (): void => {
        this.storePolygonPaths();
      }
    );
  }

  /**
   * Reset the map and controls.
   */
  onResetMapControls(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    // Remove sign-out zone circle
    if (this.signOutZoneCircle) {
      this.signOutZoneCircle.setMap(null);
      this.signOutZoneCircle = null;
    }

    if (this.polygon) {
      this.polygon.setMap(null);
    }

    // Remove polygon edit listeners
    google.maps.event.removeListener(this.coordinatesSetAtListener);
    google.maps.event.removeListener(this.coordinatesInsertAtListener);
    this.coordinatesSetAtListener = null;
    this.coordinatesInsertAtListener = null;

    // clear the site geofence
    this.site.geofence = '';
    this.site.geofence_size = 0;
    this.geofenceCoordinates.length = 0;

    // remove the polygon
    this.polygon = null;

    // Re-enter drawing mode so the user can draw a new geofence.
    this.stopDrawing();
    this.startDrawing();
  }

  /**
   * Store the polygon data as concatenated coords.
   */
  private storePolygonPaths(): void {
    if (this.polygon) {
      let geofence: string = '';
      const paths: any = this.polygon.getPath();
      let firstPath: any;

      this.geofenceCoordinates.length = 0;

      paths.forEach(
        (path: any, i: number): void => {
          geofence =
            geofence +
            parseFloat(path.lat()).toFixed(6) +
            ' ' +
            parseFloat(path.lng()).toFixed(6) +
            ',';
          if (!i) {
            firstPath = path;
          }
          this.geofenceCoordinates.push(
            new google.maps.LatLng(
              parseFloat(path.lat()).toFixed(6),
              parseFloat(path.lng()).toFixed(6)
            )
          );
        }
      );

      geofence =
        geofence +
        parseFloat(firstPath.lat()).toFixed(6) +
        ' ' +
        parseFloat(firstPath.lng()).toFixed(6);

      this.geofenceCoordinates.push(
        new google.maps.LatLng(
          parseFloat(firstPath.lat()).toFixed(6),
          parseFloat(firstPath.lng()).toFixed(6)
        )
      );

      this.site.geofence = geofence;

      this.calculateGeofenceSize();

      this.drawSignOutZoneOverlay();
    }
  }

  /**
   * Convert degrees to radians.
   */
  private toRadians(deg: number): number {
    return deg * (Math.PI / 180);
  }

  /**
   * Calculate haversine distance between two points in metres.
   */
  private haversineDistance(
    p1: { lat: number; lng: number },
    p2: { lat: number; lng: number }
  ): number {
    const R = 6371000;
    const dLat = this.toRadians(p2.lat - p1.lat);
    const dLng = this.toRadians(p2.lng - p1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(p1.lat)) *
        Math.cos(this.toRadians(p2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Get polygon centroid from LatLng array.
   */
  private getPolygonCentroid(
    coords: google.maps.LatLng[]
  ): { lat: number; lng: number } {
    let sumLat = 0,
      sumLng = 0;
    coords.forEach((c) => {
      sumLat += parseFloat(c.lat().toString());
      sumLng += parseFloat(c.lng().toString());
    });
    return { lat: sumLat / coords.length, lng: sumLng / coords.length };
  }

  /**
   * Get max distance from centroid to any vertex.
   */
  private getMaxDistanceFromCentroid(coords: google.maps.LatLng[]): number {
    const centroid = this.getPolygonCentroid(coords);
    let maxDist = 0;
    coords.forEach((c) => {
      const dist = this.haversineDistance(centroid, {
        lat: parseFloat(c.lat().toString()),
        lng: parseFloat(c.lng().toString())
      });
      if (dist > maxDist) maxDist = dist;
    });
    return maxDist;
  }

  /**
   * Draw or update the sign-out zone circle overlay on the map.
   */
  private drawSignOutZoneOverlay(): void {
    // Remove existing circle
    if (this.signOutZoneCircle) {
      this.signOutZoneCircle.setMap(null);
      this.signOutZoneCircle = null;
    }

    // Only draw when the sign-out zone is enabled and the geofence has at least three plotted points.
    // Use the polygon path length (the actual vertices the user placed) rather than
    // geofenceCoordinates, which includes the duplicate closing point and would otherwise let the
    // circle appear after only two clicks.
    const plottedPointCount: number = this.polygon
      ? this.polygon.getPath().getLength()
      : 0;
    if (!this.site.enable_sign_out_zone || plottedPointCount < 3) {
      return;
    }

    const centroid = this.getPolygonCentroid(this.geofenceCoordinates);
    const maxDistance = this.getMaxDistanceFromCentroid(
      this.geofenceCoordinates
    );
    const configuredRadius =
      maxDistance + (this.site.sign_out_zone_radius || 0);
    const radius = Math.max(configuredRadius, 200);

    this.signOutZoneCircle = new google.maps.Circle({
      strokeColor: '#ADD8E6',
      strokeOpacity: 0.4,
      strokeWeight: 2,
      fillColor: '#ADD8E6',
      fillOpacity: 0.15,
      map: this.map,
      center: { lat: centroid.lat, lng: centroid.lng },
      radius: radius,
      clickable: false
    });
  }

  /**
   * Calculates the geofence size.
   * @private
   */
  private calculateGeofenceSize(): void {
    if (this.geofenceCoordinates.length > 2) {
      this.site.geofence_size = google.maps.geometry.spherical.computeArea(
        this.geofenceCoordinates
      );
    }
  }
}
